/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import {
  Group,
  Text,
  rem,
  useMantineTheme,
  type MantineSize,
  type GroupProps,
} from '@mantine/core';

import { TextVariants } from '../typography/variants';

const specificSizes: {
  [key in MantineSize | '2xs']: {
    height: number;
  };
} = {
  '2xs': { height: 16 },
  xs: { height: 20 },
  sm: { height: 24 },
  md: { height: 32 },
  lg: { height: 40 },
  xl: { height: 40 },
};

export default function Tag({
  size = 'sm',
  icon,
  children,
  ...props
}: GroupProps & {
  size?: MantineSize | '2xs';
  icon?: React.ReactNode;
  children?: JSX.Element | string;
}) {
  const theme = useMantineTheme();
  const { height } = specificSizes[size];

  return (
    <Group
      align="center"
      bg={theme.pf.color.bg.neutral.shade.base}
      c={theme.pf.color.fg.neutral.strong}
      gap={theme.pf.layout['4xs']}
      h={height}
      px={theme.pf.layout['3xs']}
      style={{
        border: `${rem(1)} solid ${theme.pf.color.border.neutral.default}`,
        borderRadius: theme.pf.radius.sm,
      }}
      w="fit-content"
      wrap="nowrap"
      {...props}
    >
      {icon}

      {children && (
        <Text
          lh={0}
          variant={size === '2xs' || size === 'xs' ? TextVariants.Title2Xs : TextVariants.TitleXs}
          fz={size === '2xs' ? rem(10) : undefined}
        >
          {children}
        </Text>
      )}
    </Group>
  );
}

Tag.displayName = 'Tag';
